import { useCallback, useMemo, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { jwtDecode } from 'jwt-decode';
import { Eye } from 'lucide-react';
import { Navigate, useNavigate, useSearchParams } from 'react-router';

import { dashboardOptions } from '@/features/dashboard/api/dashboard.options';
import {
  buildDashboardPath,
  SAVE_SNACKBAR_KEY,
  saveDashboardToApi
} from '@/features/dashboard/api/mutations';
import { useDashboard, useSharedDashboard } from '@/features/dashboard/api/queries';
import { DashboardLayout } from '@/features/dashboard/components/DashboardLayout';
import {
  SHARE_TOKEN_PARAM,
  SHARED_DASHBOARD_EXPIRED_PATH,
  SHARED_DASHBOARD_NOT_FOUND_PATH
} from '@/features/dashboard/constants/const';
import { extractFirstDashboard } from '@/features/dashboard/lib/extractFirstDashboard';
import { getTabContent } from '@/features/dashboard/lib/transformers';
import { MarketsComponents, MarketWidgetsSkeleton, useMarketPreview } from '@/features/markets';
import useSnackbarNotifications from '@/shared/hooks/useSnackbarNotifications';
import { getUniqueName } from '@/shared/lib/format/getUniqueName';
import { PageInformationContext } from '@/shared/lib/PageInformationContext';

import { clock } from '@/services/clock';
import { SharedDashboardBanner } from './SharedDashboardBanner';

interface ShareTokenPayload {
  dashboardName: string;
  dashboardSharer: string;
  exp: number;
  iat: number;
  iss: string;
}

const ReadOnlyBox = () => (
  <div className='flex h-12 items-center justify-center gap-2 rounded-lg bg-muted px-3 text-sm font-medium text-muted-foreground'>
    <Eye
      aria-hidden='true'
      className='size-4'
    />
    <span>Read only</span>
  </div>
);

const SharedDashboard = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get(SHARE_TOKEN_PARAM);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showLoadingSnackbar, showSuccessSnackbar, showErrorSnackbar } =
    useSnackbarNotifications();
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const { data: dashboards } = useDashboard();
  const {
    data: sharedDashboardData,
    isLoading: isSharedLoading,
    isError: isSharedError
  } = useSharedDashboard(token);

  const { dashboardName: sharedName, dashboardTabs } = useMemo(
    () => extractFirstDashboard(sharedDashboardData),
    [sharedDashboardData]
  );

  const tabNames = useMemo(
    () => (dashboardTabs ? Object.keys(dashboardTabs) : []),
    [dashboardTabs]
  );

  const activeTabConfig = useMemo(() => {
    if (!dashboardTabs || tabNames.length === 0) return undefined;
    const tabKey = tabNames[activeTab];
    if (!tabKey) return undefined;
    return getTabContent(dashboardTabs[tabKey]);
  }, [dashboardTabs, tabNames, activeTab]);

  const {
    data: previewData,
    isLoading: isPreviewLoading,
    isFetching: isPreviewFetching
  } = useMarketPreview(activeTabConfig, !!activeTabConfig);

  const isLoading = isSharedLoading || isPreviewLoading || isPreviewFetching;

  const handleTabChange = useCallback((tabIndex: number) => {
    setActiveTab(tabIndex);
  }, []);

  const handleSave = async () => {
    if (!dashboardTabs || !dashboards) return;

    setIsSaving(true);
    showLoadingSnackbar('Saving dashboard, please wait...', SAVE_SNACKBAR_KEY);
    try {
      const existingNames = Object.keys(dashboards);
      const saveName = getUniqueName(existingNames, sharedName ?? 'Shared Dashboard');

      await saveDashboardToApi(saveName, dashboardTabs);
      showSuccessSnackbar('Dashboard saved!', SAVE_SNACKBAR_KEY);
      await queryClient.refetchQueries({ queryKey: dashboardOptions.all() });
      navigate(buildDashboardPath(saveName));
    } catch {
      showErrorSnackbar("Oops! We couldn't save the dashboard. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  if (isSharedError) {
    return (
      <Navigate
        replace
        to={SHARED_DASHBOARD_NOT_FOUND_PATH}
      />
    );
  }

  if (!token) {
    return (
      <Navigate
        replace
        to='/notfound'
      />
    );
  }

  let decoded: ShareTokenPayload;
  try {
    decoded = jwtDecode<ShareTokenPayload>(token);
  } catch {
    return (
      <Navigate
        replace
        to='/notfound'
      />
    );
  }

  if (decoded.exp * 1000 < clock.now()) {
    return (
      <Navigate
        replace
        to={SHARED_DASHBOARD_EXPIRED_PATH}
      />
    );
  }

  const { dashboardName: name, dashboardSharer: user } = decoded;

  return (
    <PageInformationContext value={{ mainPageTitle: 'Dashboard' }}>
      <SharedDashboardBanner
        dashboardName={name}
        isLoading={isLoading}
        isSaving={isSaving}
        user={user}
        onSave={handleSave}
      />
      <DashboardLayout
        activeTab={activeTab}
        renderRight={<ReadOnlyBox />}
        stickyHeader={false}
        tabNames={tabNames}
        title={sharedName ?? name}
        onTabChange={handleTabChange}
      >
        {isLoading ? (
          <MarketWidgetsSkeleton />
        ) : (
          activeTabConfig &&
          activeTabConfig.length > 0 &&
          previewData && (
            <MarketsComponents
              components={previewData.components}
              showAddToDashboard={false}
              showShareBlock={false}
            />
          )
        )}
      </DashboardLayout>
    </PageInformationContext>
  );
};

export default SharedDashboard;
